/**
 * useChatCitationClick — shared navigator for inline `[N]` citation clicks
 * in scoped chat threads (per-issue, per-clause, per-finding). Behaviour
 * port of RightPanelChat.handleCitationClick (Plan 159 / Issue #761) so the
 * scoped threads match the general chat exactly.
 *
 * Critical guards preserved:
 *   - Matter branch: outer `if (citation.clause_id)` — without a clause id
 *     we have no target for `setPendingClauseId` (typed `string | null`),
 *     so the whole branch is skipped to avoid an invalid pending id.
 *   - Workbench branch: inner `if (citation.clause_id)` — opening the
 *     precedent tab is valid even when the citation points at the doc as
 *     a whole; the clause-scroll step is conditional.
 */
import { useCallback } from 'react';
import { useDocumentStore } from '../stores/documentStore';
import { useDeskStore } from '../stores/deskStore';
import type { Citation } from '../components/chat/types';

export function useChatCitationClick() {
  const selectedMatterId = useDeskStore((s) => s.selectedMatterId);

  return useCallback(
    (citation: Citation) => {
      // Plan 42 Task 5 — bail when no matter is selected. The openDocument
      // guard rejects empty matterIds, so `|| ''` would silently no-op.
      // For full fail-closed verification (resolve doc's project_id via
      // apiService.getDocument and compare post-fetch to defend against
      // mid-fetch matter switches), see useDocumentContent.ts's ownership
      // gate — it auto-closes any tab that opens here and turns out to
      // belong to a different matter. Deferred-work follow-up: surface a
      // UI hint instead of the brief tab-flash.
      if (!selectedMatterId) return;

      const effectiveType = citation.source_type ?? 'matter';
      const docStore = useDocumentStore.getState();

      if (effectiveType === 'workbench' && citation.workbench_document_id) {
        docStore.openDocument({
          id: citation.workbench_document_id,
          name: citation.workbench_document_title ?? 'Precedent',
          matterId: selectedMatterId,
          type: 'workbench',
        });
        if (citation.clause_id) {
          docStore.setPendingClauseId(citation.clause_id);
        }
        return;
      }

      if (citation.clause_id) {
        if (
          citation.document_id &&
          citation.document_id !== docStore.activeDocumentId
        ) {
          docStore.openDocument({
            id: citation.document_id,
            name: citation.document_name ?? 'Document',
            matterId: selectedMatterId,
            type: 'document',
          });
        }
        docStore.setPendingClauseId(citation.clause_id);
      }
    },
    [selectedMatterId],
  );
}
